import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowRight, Scissors, Shield, Globe, Truck, Users,
  MapPin, Phone, Mail, Factory, CheckCircle2,
  Layers, Target, Building2, Package, ArrowDown
} from 'lucide-react';

const garmentCategories = [
  { icon: Users, title: "Men's Wear (40%)", desc: "Shirts, T-shirts, Polos, Casual & Formal Wear", image: "/cutting-floor.jpg" },
  { icon: Package, title: "Kids Wear (30%)", desc: "Scalable production for retail-focused categories", image: "/stitching-floor.jpg" },
  { icon: Scissors, title: "Women's Wear (30%)", desc: "Dresses, Tops, Co-ords, Everyday Fashion", image: "/quality-check.jpg" },
];

const productionCaps = [
  { icon: Layers, title: "Knit & Woven Manufacturing" },
  { icon: Target, title: "Sampling & Product Development" },
  { icon: Factory, title: "Bulk Production (5000+ pcs per style)" },
  { icon: Truck, title: "Fabric Sourcing & Vendor Coordination" },
  { icon: Building2, title: "Centralized Production Planning" },
  { icon: Globe, title: "Multi-unit Execution with Single Control" },
];

const whyBrands = [
  { title: "Scale Without Complexity", desc: "Execute large orders across multiple units with centralized control" },
  { title: "Speed & Reliability", desc: "Faster turnaround through parallel production systems" },
  { title: "Consistent Quality", desc: "Standardized QC processes across all facilities" },
  { title: "Production Visibility", desc: "Structured tracking from sampling to dispatch" },
  { title: "Built for Retail Brands", desc: "Experience working with high-demand, compliance-driven clients" },
];

const statsData = [
  { value: "13+", label: "Years of Excellence" },
  { value: "10L+", label: "Pieces Monthly" },
  { value: "16", label: "Manufacturing Units" },
  { value: "2500+", label: "Skilled Workforce" },
];

const processSteps = ["Sampling", "Fabric Sourcing", "Cutting", "Stitching", "Finishing", "Quality Check", "Packing", "Dispatch"];

const complianceItems = [
  "SEDEX Compliant Manufacturing",
  "Inline + Final Quality Inspection",
  "Standardized Processes Across Units",
  "Production Monitoring & Reporting Systems",
];

/* Animated Counter */
function AnimatedStat({ value, label, visible }) {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    if (!visible) return;
    const numMatch = value.match(/^(\d+)/);
    if (!numMatch) { setDisplay(value); return; }
    const target = parseInt(numMatch[1]);
    const suffix = value.slice(numMatch[1].length);
    let current = 0;
    const duration = 1600;
    const steps = 40;
    const increment = target / steps;
    const interval = duration / steps;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      setDisplay(Math.round(current) + suffix);
    }, interval);
    return () => clearInterval(timer);
  }, [visible, value]);
  return (
    <div className="stat-counter text-center lg:text-left">
      <p className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white/90 leading-none">{display}</p>
      <p className="text-[11px] tracking-[0.25em] uppercase text-white/35 mt-3 font-medium">{label}</p>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inquiry, setInquiry] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const handleInquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/inquiries', inquiry);
      toast.success('Inquiry submitted successfully!');
      setInquiry({ name: '', email: '', company: '', phone: '', message: '' });
    } catch {
      toast.error('Failed to submit inquiry');
    }
    setSending(false);
  };

  const setRef = useCallback((id) => (el) => { sectionRefs.current[id] = el; }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 glass-header-dark ${scrolled ? 'scrolled' : ''}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo-icon.png" alt="Paridhi Apparels" className="h-8 w-auto" />
            <h1 className="font-heading text-xl font-semibold tracking-tight logo-text-animated-light">
              Paridhi Apparels
            </h1>
          </Link>
          <div className="hidden md:flex items-center gap-10">
            <a href="#about" className="text-[13px] font-medium text-white/50 hover:text-white transition-colors duration-300">About</a>
            <a href="#capabilities" className="text-[13px] font-medium text-white/50 hover:text-white transition-colors duration-300">Capabilities</a>
            <a href="#infrastructure" className="text-[13px] font-medium text-white/50 hover:text-white transition-colors duration-300">Infrastructure</a>
            <a href="#contact" className="text-[13px] font-medium text-white/50 hover:text-white transition-colors duration-300">Contact</a>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-white text-[#111111] hover:bg-white/90 rounded-full px-6 py-2 text-[13px] font-semibold btn-premium" data-testid="go-to-dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} className="btn-gradient-animated rounded-full px-6 py-2 text-[13px] font-semibold btn-premium" data-testid="track-order-btn">
                Track Your Order
                <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <section className="hero-section min-h-screen flex items-end relative grain-overlay" data-testid="hero-section">
        {/* Hero Background Image */}
        <div className="absolute inset-0 z-0">
          <img src="/stitching-hero.jpg" alt="Paridhi Apparels Manufacturing Floor" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        </div>

        <div className="hero-content w-full max-w-[1400px] mx-auto px-6 lg:px-16 pb-20 lg:pb-28 pt-40">
          <div className="max-w-3xl">
            <p className="overline animate-fade-in-up">Engineering Garments at Scale, With Absolute Control</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white mt-8 leading-[1.05] tracking-tight animate-fade-in-up animation-delay-100">
              Precision Manufacturing.
              <span className="block font-semibold italic mt-2">Scaled for Modern Fashion Brands.</span>
            </h1>
            <p className="text-base lg:text-lg text-white/50 mt-10 max-w-xl leading-[1.9] animate-fade-in-up animation-delay-200">
              From concept to bulk execution, Paridhi Apparels delivers speed, consistency, and reliability across a fully owned manufacturing network &mdash; built for brands that demand performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-14 animate-fade-in-up animation-delay-300">
              <Button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-[#111111] hover:bg-white/90 rounded-full px-10 py-7 text-sm font-semibold tracking-wide btn-premium"
                data-testid="hero-start-production-btn"
              >
                Start Production
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-white/15 text-white hover:bg-white/[0.06] rounded-full px-10 py-7 text-sm font-semibold tracking-wide transition-all duration-300"
                data-testid="hero-track-order-btn"
              >
                Track Your Order
              </Button>
            </div>
          </div>

          {/* Stats Row */}
          <div
            id="hero-stats"
            ref={setRef('hero-stats')}
            className="grid grid-cols-2 md:grid-cols-4 gap-12 mt-28 pt-12 border-t border-white/[0.06] animate-fade-in-up animation-delay-500"
          >
            {statsData.map((s) => (
              <AnimatedStat key={s.label} value={s.value} label={s.label} visible={visibleSections['hero-stats'] || true} />
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-fade-in animation-delay-800">
          <div className="flex flex-col items-center gap-2 opacity-40">
            <ArrowDown className="w-4 h-4 text-white animate-bounce" />
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section
        id="about"
        ref={setRef('about')}
        className={`py-32 lg:py-48 bg-white section-reveal ${visibleSections.about ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-center">
            <div>
              <p className="overline">About Us</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#111111] mt-5 tracking-tight leading-[1.1]">
                Built for Scale,<br />Speed & Consistency
              </h2>
              <div className="w-16 h-[2px] bg-[#C2A878] mt-8" />
              <p className="text-base text-[#52525B] mt-8 leading-[1.9]">
                Paridhi Apparels Pvt. Ltd. is a vertically controlled garment manufacturing company built for scale, speed, and consistency.
              </p>
              <p className="text-base text-[#52525B] mt-5 leading-[1.9]">
                With 13+ years of experience and a fully owned network of 16 manufacturing units across Bhiwandi, Kalher, and Sonale, we execute over 1 million garments every month for leading fashion retailers and private labels.
              </p>
              <p className="text-base text-[#52525B] mt-5 leading-[1.9]">
                Our strength lies in control &mdash; from fabric sourcing to final dispatch &mdash; ensuring every order meets timelines, quality benchmarks, and scale requirements without compromise.
              </p>
              <div className="mt-12 pt-8 border-t border-black/[0.06]">
                <p className="text-sm font-semibold text-[#111111] italic leading-relaxed">
                  We are not just manufacturers. We are execution partners for growing fashion brands.
                </p>
              </div>
            </div>
            <div className="h-[500px] lg:h-[600px] rounded-xl overflow-hidden">
              <img src="/factory-night.jpg" alt="Paridhi Apparels Factory" className="w-full h-full object-cover rounded-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ CAPABILITIES ============ */}
      <section
        id="capabilities"
        ref={setRef('capabilities')}
        className={`py-32 lg:py-48 bg-[var(--warm-white,#FAFAF8)] section-reveal ${visibleSections.capabilities ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16">
          <div className="max-w-2xl">
            <p className="overline">What We Do</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#111111] mt-5 tracking-tight">
              End-to-End Garment Manufacturing
            </h2>
          </div>

          {/* Category Cards */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 mt-20 stagger-children ${visibleSections.capabilities ? 'visible' : ''}`}>
            {garmentCategories.map((cat) => (
              <div key={cat.title} className="bento-card rounded-xl p-8 lg:p-10 bg-white">
                <div className="w-12 h-12 rounded-xl bg-[var(--fog,#F2F2EF)] flex items-center justify-center mb-8">
                  <cat.icon className="w-5 h-5 text-[#111111]" />
                </div>
                <h3 className="text-base font-semibold text-[#111111]">{cat.title}</h3>
                <p className="text-sm text-[#52525B] mt-3 leading-relaxed">{cat.desc}</p>
                <div className="mt-6 rounded-lg h-40 overflow-hidden">
                  <img src={cat.image} alt={cat.title} className="w-full h-full object-cover rounded-lg" />
                </div>
              </div>
            ))}
          </div>

          {/* Production Capabilities */}
          <h3 className="text-lg font-semibold text-[#111111] mt-24 mb-8">Production Capabilities</h3>
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children ${visibleSections.capabilities ? 'visible' : ''}`}>
            {productionCaps.map((cap) => (
              <div key={cap.title} className="flex items-center gap-4 bg-white rounded-xl p-5 border border-black/[0.05] hover:border-[#C2A878]/30 hover:shadow-sm transition-all duration-300">
                <div className="w-10 h-10 rounded-xl bg-[var(--fog,#F2F2EF)] flex items-center justify-center flex-shrink-0">
                  <cap.icon className="w-4 h-4 text-[var(--olive,#6B7D5C)]" />
                </div>
                <span className="text-sm font-medium text-[#111111]">{cap.title}</span>
              </div>
            ))}
          </div>

          {/* Operational Strength */}
          <h3 className="text-lg font-semibold text-[#111111] mt-24 mb-8">Operational Strength</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              "Fully Owned Manufacturing Network (No dependency on outsourcing)",
              "High-volume execution with timeline control",
              "Structured workflows for large retail orders",
              "Consistent quality across all units"
            ].map((item) => (
              <div key={item} className="flex items-start gap-4 bg-white rounded-xl p-5 border border-black/[0.05] hover:border-[var(--olive,#6B7D5C)]/20 transition-all duration-300">
                <CheckCircle2 className="w-4 h-4 text-[var(--olive,#6B7D5C)] mt-0.5 flex-shrink-0" />
                <span className="text-sm font-medium text-[#111111]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY BRANDS ============ */}
      <section
        id="why"
        ref={setRef('why')}
        className={`py-32 lg:py-48 bg-white section-reveal ${visibleSections.why ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-start">
            <div className="lg:sticky lg:top-32">
              <p className="overline">Why Us</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#111111] mt-5 tracking-tight">
                Why Brands Work With Us
              </h2>
              <div className="w-16 h-[2px] bg-[#C2A878] mt-8" />
              <div className="mt-12 rounded-xl hidden lg:block overflow-hidden">
                <img src="/final-checking.jpg" alt="Quality Control Process" className="w-full h-auto rounded-xl object-cover" style={{ aspectRatio: '16/9' }} />
              </div>
            </div>
            <div className={`space-y-5 stagger-children ${visibleSections.why ? 'visible' : ''}`}>
              {whyBrands.map((item, i) => (
                <div key={item.title} className="bento-card rounded-xl p-8 bg-white">
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 rounded-xl bg-[#C2A878]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-heading font-bold text-[#C2A878]">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-[#111111]">{item.title}</h3>
                      <p className="text-sm text-[#52525B] mt-2 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ INFRASTRUCTURE ============ */}
      <section
        id="infrastructure"
        ref={setRef('infrastructure')}
        className={`py-32 lg:py-48 bg-[var(--warm-white,#FAFAF8)] section-reveal ${visibleSections.infrastructure ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-center">
            {/* Image Left */}
            <div className="relative order-2 lg:order-1">
              <div className="h-[500px] lg:h-[600px] rounded-xl overflow-hidden">
                <img src="/auto-machines.jpg" alt="Paridhi Apparels Manufacturing Equipment" className="w-full h-full object-cover rounded-xl" />
              </div>
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-xl rounded-xl p-5 border border-black/5 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-semibold tracking-[0.1em] uppercase text-[#111111]">16 Units Active Across 7 Facilities</span>
                </div>
              </div>
            </div>
            {/* Text Right */}
            <div className="order-1 lg:order-2">
              <p className="overline">Our Network</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#111111] mt-5 tracking-tight leading-[1.1]">
                Manufacturing<br />Ecosystem
              </h2>
              <div className="w-16 h-[2px] bg-[#C2A878] mt-8" />
              <p className="text-base text-[#52525B] mt-8 leading-[1.9]">
                Our manufacturing ecosystem is built across 7 large facilities housing 16 operational units in Bhiwandi, Kalher, and Sonale (Mumbai, Maharashtra).
              </p>
              <p className="text-base text-[#52525B] mt-5 leading-[1.9]">
                Each unit is optimized for specific production stages &mdash; enabling efficient, high-volume output while maintaining strict quality control across the entire system.
              </p>
              <div className="space-y-4 mt-12">
                {[
                  "7 facilities with 16 operational units",
                  "Locations: Bhiwandi, Kalher & Sonale",
                  "Optimized for stage-specific production",
                  "2500+ skilled workforce across all units",
                  "Centralized control with distributed execution"
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[var(--olive,#6B7D5C)] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#111111] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMPLIANCE ============ */}
      <section
        id="compliance"
        ref={setRef('compliance')}
        className={`py-32 lg:py-48 bg-white section-reveal ${visibleSections.compliance ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16 text-center">
          <p className="overline">Quality Assurance</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#111111] mt-5 tracking-tight">
            Compliance & Quality
          </h2>
          <p className="text-base text-[#52525B] mt-5 max-w-2xl mx-auto leading-relaxed">
            Every garment passes through structured quality checkpoints to ensure consistency at scale.
          </p>
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-20 stagger-children ${visibleSections.compliance ? 'visible' : ''}`}>
            {complianceItems.map((item) => (
              <div key={item} className="bento-card rounded-xl p-8 bg-white text-center">
                <div className="w-14 h-14 rounded-2xl bg-[var(--olive,#6B7D5C)]/10 flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(107,125,92,0.08)' }}>
                  <Shield className="w-6 h-6 text-[var(--olive,#6B7D5C)]" />
                </div>
                <p className="text-sm font-semibold text-[#111111] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section
        id="process"
        ref={setRef('process')}
        className={`py-32 lg:py-48 bg-[#0A0A0A] grain-overlay section-reveal ${visibleSections.process ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16 text-center relative z-10">
          <p className="overline">Our Process</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mt-5 tracking-tight">
            Streamlined Production Flow
          </h2>
          <p className="text-base text-white/40 mt-5 max-w-2xl mx-auto leading-relaxed">
            A streamlined production flow designed for scale and transparency. Every stage is tracked, monitored, and optimized for timely delivery.
          </p>
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-20 stagger-children ${visibleSections.process ? 'visible' : ''}`}>
            {processSteps.map((step, i) => (
              <div key={step} className="process-step">
                <p className="step-num">{String(i + 1).padStart(2, '0')}</p>
                <p className="text-sm font-semibold text-white/90">{step}</p>
              </div>
            ))}
          </div>

          {/* Production Gallery */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { src: "/fabric-inspection.jpg", label: "Fabric Inspection" },
              { src: "/cutting-floor-2.jpg", label: "Cutting" },
              { src: "/stitching-floor-2.jpg", label: "Stitching" },
              { src: "/packing-dispatch.jpg", label: "Packing & Dispatch" },
            ].map((item) => (
              <div key={item.label} className="relative rounded-xl overflow-hidden group" style={{ aspectRatio: '4/3' }}>
                <img src={item.src} alt={item.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <p className="absolute bottom-3 left-3 text-[10px] font-semibold text-white/80 uppercase tracking-wider">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLIENTS / TRUST ============ */}
      <section
        id="clients"
        ref={setRef('clients')}
        className={`py-32 lg:py-48 bg-[#0A0A0A] grain-overlay section-reveal ${visibleSections.clients ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16 text-center relative z-10">
          <p className="overline">Trusted By</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mt-5 tracking-tight">
            Brands That Trust Our Execution
          </h2>
          <p className="text-base text-white/40 mt-6 max-w-xl mx-auto leading-relaxed">
            Delivering high-volume, compliance-driven manufacturing for India's leading retail chains and private labels.
          </p>

          {/* Current Clients - Large Bubbles */}
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/25 mt-20 mb-8">Current Partners</p>
          <div className="flex flex-wrap justify-center gap-6 lg:gap-10 mb-16">
            {[
              { name: "Zudio", logo: "/brands/zudio.jpg", delay: "0s", fill: false },
              { name: "OWND", logo: "/brands/ownd.jpg", delay: "1.5s", fill: false },
              { name: "The Souled Store", logo: "/brands/souledstore.png", delay: "3s", fill: true },
              { name: "Global Desi", logo: "/brands/globaldesi.jpg", delay: "0.8s", fill: true },
            ].map((brand) => (
              <div
                key={brand.name}
                className="brand-bubble brand-bubble-lg"
                style={{ animationDelay: brand.delay }}
                title={brand.name}
                data-testid={`brand-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <img src={brand.logo} alt={brand.name} className={brand.fill ? 'brand-fill' : ''} />
              </div>
            ))}
          </div>

          {/* Past Clients - Smaller Bubbles */}
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/20 mb-8">Past Collaborations</p>
          <div className="flex flex-wrap justify-center gap-4 lg:gap-6 max-w-4xl mx-auto">
            {[
              { name: "FirstCry", logo: "/brands/firstcry.jpg", delay: "0.3s", fill: true },
              { name: "Pantaloons", logo: "/brands/pantaloons.jpg", delay: "2.1s", fill: true },
              { name: "Shoppers Stop", logo: "/brands/shoppersstop.jpg", delay: "0.9s", fill: true },
              { name: "DMart", logo: "/brands/dmart.png", delay: "2.8s", fill: false },
              { name: "Spencer's", logo: "/brands/spencers.jpg", delay: "1.4s", fill: true },
              { name: "Gini & Jony", logo: "/brands/ginijony.jpg", delay: "3.5s", fill: true },
              { name: "Toonz", logo: "/brands/toonz.jpg", delay: "0.6s", fill: true },
              { name: "Fusion Beats", logo: "/brands/fusionbeats.jpg", delay: "2.4s", fill: false },
              { name: "FCUK", logo: "/brands/fcuk.jpg", delay: "1.8s", fill: false },
            ].map((brand) => (
              <div
                key={brand.name}
                className="brand-bubble brand-bubble-sm"
                style={{ animationDelay: brand.delay }}
                title={brand.name}
                data-testid={`brand-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <img src={brand.logo} alt={brand.name} className={brand.fill ? 'brand-fill' : ''} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ DASHBOARD PREVIEW ============ */}
      <section
        id="dashboard-preview"
        ref={setRef('dashboard-preview')}
        className={`py-32 lg:py-48 bg-[var(--warm-white,#FAFAF8)] section-reveal ${visibleSections['dashboard-preview'] ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <p className="overline">Production Portal</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#111111] mt-5 tracking-tight">
              Real-Time Order Tracking
            </h2>
          </div>
          <div className="dashboard-preview p-6 lg:p-8 max-w-4xl mx-auto">
            {/* Mock dashboard header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500/60" />
                <div className="w-3 h-3 rounded-full bg-amber-500/60" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/60" />
              </div>
              <p className="text-[10px] text-white/30 tracking-wider uppercase font-medium">Paridhi Apparels Portal</p>
            </div>
            {/* Mock table rows */}
            <div className="space-y-3">
              {[
                { po: "5200658925", style: "WD14 Shirt", buyer: "ZUDIO", progress: 44, status: "At Risk" },
                { po: "5200658930", style: "WD29 Shirt", buyer: "ZUDIO", progress: 22, status: "On Track" },
                { po: "6200456712", style: "Polo T-shirt", buyer: "STYLE UP", progress: 66, status: "On Track" },
              ].map((row) => (
                <div key={row.po} className="flex items-center gap-4 bg-white/[0.04] rounded-lg px-5 py-4 border border-white/[0.04]">
                  <span className="text-xs font-semibold text-white/70 w-24 truncate">{row.po}</span>
                  <span className="text-xs text-white/50 flex-1 truncate">{row.style}</span>
                  <span className="text-xs text-white/40 w-20 hidden sm:block">{row.buyer}</span>
                  <div className="w-32 hidden md:block">
                    <div className="preview-bar h-[5px] rounded-full" style={{ '--progress': `${row.progress}%` }} />
                  </div>
                  <span className="text-[11px] font-semibold text-white/60 w-10 text-right">{row.progress}%</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${row.status === 'At Risk' ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                    {row.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section
        id="contact"
        ref={setRef('contact')}
        className={`py-32 lg:py-48 bg-white section-reveal ${visibleSections.contact ? 'visible' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32">
            <div>
              <p className="overline">Start Production</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#111111] mt-5 tracking-tight leading-[1.1]">
                Looking for a Reliable<br />Manufacturing Partner?
              </h2>
              <div className="w-16 h-[2px] bg-[#C2A878] mt-8" />
              <p className="text-base text-[#52525B] mt-8 leading-[1.9] max-w-md">
                Scale your production with a team built for execution, speed, and consistency. Submit an inquiry and our team will get back within 24 hours.
              </p>
              <div className="space-y-5 mt-12">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#C2A878]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#C2A878]" />
                  </div>
                  <span className="text-sm text-[#52525B]">Bhiwandi, Maharashtra, India</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#C2A878]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-[#C2A878]" />
                  </div>
                  <span className="text-sm text-[#52525B]">info@paridhiapparels.com</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#C2A878]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-[#C2A878]" />
                  </div>
                  <span className="text-sm text-[#52525B]">+91 XXXX XXXXXX</span>
                </div>
              </div>
            </div>
            <form onSubmit={handleInquiry} className="space-y-5 bg-[var(--warm-white,#FAFAF8)] rounded-2xl p-8 lg:p-10 border border-black/[0.04]" data-testid="inquiry-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  placeholder="Your Name"
                  value={inquiry.name}
                  onChange={(e) => setInquiry(p => ({ ...p, name: e.target.value }))}
                  required
                  className="bg-white border-black/[0.06] text-[#111111] placeholder:text-[#A1A1AA] h-12 rounded-xl focus:border-[#C2A878] focus:ring-[#C2A878]/20"
                  data-testid="inquiry-name"
                />
                <Input
                  placeholder="Email"
                  type="email"
                  value={inquiry.email}
                  onChange={(e) => setInquiry(p => ({ ...p, email: e.target.value }))}
                  required
                  className="bg-white border-black/[0.06] text-[#111111] placeholder:text-[#A1A1AA] h-12 rounded-xl focus:border-[#C2A878] focus:ring-[#C2A878]/20"
                  data-testid="inquiry-email"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  placeholder="Company"
                  value={inquiry.company}
                  onChange={(e) => setInquiry(p => ({ ...p, company: e.target.value }))}
                  className="bg-white border-black/[0.06] text-[#111111] placeholder:text-[#A1A1AA] h-12 rounded-xl focus:border-[#C2A878] focus:ring-[#C2A878]/20"
                  data-testid="inquiry-company"
                />
                <Input
                  placeholder="Phone"
                  value={inquiry.phone}
                  onChange={(e) => setInquiry(p => ({ ...p, phone: e.target.value }))}
                  className="bg-white border-black/[0.06] text-[#111111] placeholder:text-[#A1A1AA] h-12 rounded-xl focus:border-[#C2A878] focus:ring-[#C2A878]/20"
                  data-testid="inquiry-phone"
                />
              </div>
              <textarea
                placeholder="Tell us about your production requirements..."
                value={inquiry.message}
                onChange={(e) => setInquiry(p => ({ ...p, message: e.target.value }))}
                required
                rows={5}
                className="w-full bg-white border border-black/[0.06] text-[#111111] placeholder:text-[#A1A1AA] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C2A878]/20 focus:border-[#C2A878] transition-all"
                data-testid="inquiry-message"
              />
              <Button
                type="submit"
                disabled={sending}
                className="bg-[#111111] hover:bg-black text-white rounded-full px-10 py-7 text-sm font-semibold tracking-wide w-full sm:w-auto btn-premium"
                data-testid="inquiry-submit-btn"
              >
                {sending ? 'Sending...' : 'Start Your Production'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-[#0A0A0A] py-12 border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-icon.png" alt="Paridhi Apparels" className="h-6 w-auto brightness-0 invert opacity-80" />
            <div>
              <p className="text-sm font-heading font-semibold tracking-tight logo-text-animated-light">Paridhi Apparels Pvt. Ltd.</p>
              <p className="text-xs text-white/25 mt-1">Manufacturing at Scale. Delivering with Precision.</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/login" className="text-xs text-white/25 hover:text-white/50 transition-colors duration-300" data-testid="footer-login-link">
              Portal Login
            </Link>
            <p className="text-xs text-white/15">
              &copy; {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
